import type { ReactNode } from 'react'

import { parseMarkdown, type Block, type Inline } from '../../lib/assistantMarkdown'

/**
 * A reply's words, with the safe Markdown subset `lib/assistantMarkdown.ts`
 * understands (owner, 2026-10-12). Every node is a React element built from
 * plain data -- never `dangerouslySetInnerHTML`, never an `<a>` or `<img>` --
 * so whatever the model writes is shown as text. A heading is a bold
 * paragraph; a fenced block is plain preformatted text.
 */
export function ReplyText({ text }: { text: string }) {
  return <div className="assistant-msg__text assistant-md">{parseMarkdown(text).map(block)}</div>
}

function block(b: Block, key: number): ReactNode {
  switch (b.kind) {
    case 'p':
      return <p key={key}>{inlines(b.children)}</p>
    case 'heading':
      return (
        <p key={key}>
          <strong>{inlines(b.children)}</strong>
        </p>
      )
    case 'ul':
      return <ul key={key}>{b.items.map(item)}</ul>
    case 'ol':
      return (
        <ol key={key} start={b.start === 1 ? undefined : b.start}>
          {b.items.map(item)}
        </ol>
      )
    case 'pre':
      return <pre key={key}>{b.text}</pre>
  }
}

function item(children: Inline[], key: number): ReactNode {
  return <li key={key}>{inlines(children)}</li>
}

function inlines(parts: Inline[]): ReactNode[] {
  return parts.map((part, key) => {
    switch (part.kind) {
      case 'text':
        return part.text
      case 'br':
        return <br key={key} />
      case 'code':
        return <code key={key}>{part.text}</code>
      case 'strong':
        return <strong key={key}>{inlines(part.children)}</strong>
      case 'em':
        return <em key={key}>{inlines(part.children)}</em>
    }
  })
}
